// Node Configuration Panel
// Comprehensive configuration panel for all pipeline node types

import { useEffect, useState } from 'react';
import { PipelineNode, PipelineNodeType } from '../types/pipeline';
import { useDataSourceQueryHints, useDataSources } from '@/hooks/use-data-sources';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { FieldSelector } from '@/components/FieldSelector';
import {
  Database,
  Wand2,
  Filter,
  BarChart3,
  GitBranch,
  Layers,
  Upload,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Eye,
  Zap,
  Server,
} from 'lucide-react';

interface NodeConfigPanelProps {
  node: PipelineNode | null;
  pipelineId: string;
  edges?: Array<{ id: string; sourceId: string; targetId: string }>;
  onUpdate: (id: string, config: Record<string, unknown>) => void;
  // Optional async persistence hook. When provided, "Save Configuration"
  // applies the local change AND triggers a server-side persist (PUT
  // /pipelines/:id) so node config no longer requires a separate global save.
  onCommit?: (id: string, patch: Record<string, unknown>) => Promise<void>;
  onValidate?: (node: PipelineNode) => boolean;
  onPreview?: (nodeId: string) => void;
}

const NODE_TYPE_CONFIG: Record<
  PipelineNodeType,
  { label: string; icon: any; color: string; description: string }
> = {
  SOURCE: {
    label: 'Source',
    icon: Database,
    color: 'blue',
    description: 'Extract data from a data source',
  },
  TRANSFORMATION: {
    label: 'Transformation',
    icon: Wand2,
    color: 'amber',
    description: 'Transform and clean data',
  },
  FILTER: {
    label: 'Filter',
    icon: Filter,
    color: 'purple',
    description: 'Filter records based on conditions',
  },
  AGGREGATION: {
    label: 'Aggregation',
    icon: BarChart3,
    color: 'green',
    description: 'Aggregate and group data',
  },
  JOIN: {
    label: 'Join',
    icon: GitBranch,
    color: 'orange',
    description: 'Join multiple data sources',
  },
  UNION: {
    label: 'Union',
    icon: Layers,
    color: 'teal',
    description: 'Stack/append multiple datasets',
  },
  DESTINATION: {
    label: 'Destination',
    icon: Upload,
    color: 'emerald',
    description: 'Load transformed data to warehouse',
  },
  MULTI_SPLIT: {
    label: 'Tier Split',
    icon: Zap,
    color: 'red',
    description: 'Route to Hot/Warm/Cold stores',
  },
  TIERED_DESTINATION: {
    label: 'Tiered Store',
    icon: Server,
    color: 'indigo',
    description: 'Redis/PostgreSQL/ClickHouse',
  },
};

export default function NodeConfigPanel({
  node,
  pipelineId,
  edges = [],
  onUpdate,
  onCommit,
  onValidate,
  onPreview,
}: NodeConfigPanelProps) {
  const { data: dataSourceData } = useDataSources({ page: 1, limit: 100 });
  const dataSources = dataSourceData?.dataSources || [];

  // Local state for configuration
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [isValid, setIsValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (node) {
      setLabel(node.data.label || '');
      setDescription(node.data.description || '');
      setConfig((node.data.config || {}) as Record<string, unknown>);
      if (onValidate) {
        setIsValid(onValidate(node));
      }
    }
    // Intentionally omit onValidate from deps. Parent passes an inline arrow
    // for onValidate, which re-creates on every render — including renders
    // triggered by handleConfigChange itself. If we depended on onValidate,
    // every keystroke / dropdown change would re-run this effect and overwrite
    // the in-progress local config back to node.data.config (e.g. JOIN node's
    // joinType dropdown stuck at INNER no matter what the user picks).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);

  if (!node) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-muted p-4">
            <Wand2 className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">No Node Selected</h3>
          <p className="text-sm text-muted-foreground">
            Select a node from the canvas to configure its settings
          </p>
        </CardContent>
      </Card>
    );
  }

  const nodeTypeConfig = NODE_TYPE_CONFIG[node.data.type as PipelineNodeType];
  const NodeIcon = nodeTypeConfig.icon;

  const handleSave = async () => {
    const patch = { label, description, config };
    onUpdate(node.id, patch);
    if (onCommit) {
      try {
        setIsSaving(true);
        await onCommit(node.id, patch);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleConfigChange = (key: string, value: unknown) => {
    setConfig((prev) => {
      const newConfig = { ...prev, [key]: value };

      // Auto-validate when config changes
      if (onValidate && node) {
        const testNode = {
          ...node,
          data: {
            ...node.data,
            config: newConfig,
          },
        };
        setIsValid(onValidate(testNode));
      }

      return newConfig;
    });
  };

  return (
    <Card className="h-full overflow-auto">
      <CardHeader className="border-b">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div
              className={`rounded-full bg-${nodeTypeConfig.color}-100 p-2 text-${nodeTypeConfig.color}-700`}
            >
              <NodeIcon className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="text-lg">{nodeTypeConfig.label} Node</CardTitle>
              <CardDescription>{nodeTypeConfig.description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onPreview && node && node.data?.type === 'SOURCE' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Auto-save config before preview
                  handleSave();
                  // Small delay to ensure save completes
                  setTimeout(() => onPreview(node.id), 100);
                }}
                className="gap-2"
                disabled={!isValid}
              >
                <Eye className="h-4 w-4" />
                Preview Data
              </Button>
            )}
            <Badge variant={isValid ? 'default' : 'secondary'}>
              {isValid ? (
                <>
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  Valid
                </>
              ) : (
                <>
                  <XCircle className="mr-1 h-3 w-3" />
                  Invalid
                </>
              )}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Basic Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Basic Settings</h3>
          <div className="space-y-2">
            <Label htmlFor="node-label">Label</Label>
            <Input
              id="node-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Enter node label"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="node-description">Description</Label>
            <Textarea
              id="node-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this node does"
            />
          </div>
        </div>

        {/* Node-Specific Configuration */}
        {node.data.type === 'SOURCE' && <SourceNodeConfigSection config={config} dataSources={dataSources} onChange={handleConfigChange} />}
        {node.data.type === 'TRANSFORMATION' && <TransformationNodeConfigSection config={config} onChange={handleConfigChange} />}
        {node.data.type === 'FILTER' && <FilterNodeConfigSection config={config} onChange={handleConfigChange} />}
        {node.data.type === 'AGGREGATION' && <AggregationNodeConfigSection config={config} onChange={handleConfigChange} />}
        {node.data.type === 'JOIN' && <JoinNodeConfigSection config={config} onChange={handleConfigChange} pipelineId={pipelineId} nodeId={node.id} edges={edges} />}
        {node.data.type === 'UNION' && <UnionNodeConfigSection config={config} onChange={handleConfigChange} nodeId={node.id} edges={edges} />}
        {node.data.type === 'DESTINATION' && <DestinationNodeConfigSection config={config} onChange={handleConfigChange} />}

        {/* Save Button */}
        <Button onClick={handleSave} className="w-full" disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </CardContent>
    </Card>
  );
}

// Source Node Configuration
function SourceNodeConfigSection({
  config,
  dataSources,
  onChange,
}: {
  config: Record<string, unknown>;
  dataSources: any[];
  onChange: (key: string, value: unknown) => void;
}) {
  const selectedDataSourceId = (config.dataSourceId as string) || '';
  const { data: queryHints, isLoading: isQueryHintsLoading } = useDataSourceQueryHints(selectedDataSourceId);

  const hints = queryHints?.hints;
  const availableObjects = hints?.availableObjects || [];
  const fromObjects = hints?.fromObjects || [];
  const queryMode = hints?.queryMode || 'SQL';
  const suggestedObject = hints?.defaultObject || availableObjects[0] || fromObjects[0] || '';
  const fallbackSQLTemplate = suggestedObject
    ? `SELECT *\nFROM ${suggestedObject}\nLIMIT 100;`
    : '';
  const queryTemplate = hints?.queryTemplate || fallbackSQLTemplate;
  const referenceTitle = queryMode === 'REST'
    ? 'Endpoint/Resource Reference'
    : queryMode === 'GRAPHQL'
      ? 'GraphQL Reference'
      : 'Table/View Reference';
  const availableObjectsLabel = queryMode === 'REST'
    ? 'Available endpoint/resource hints'
    : queryMode === 'GRAPHQL'
      ? 'Available root fields'
      : 'Available table/view names for FROM';
  const exampleLabel = queryMode === 'REST'
    ? 'JSON query config example'
    : queryMode === 'GRAPHQL'
      ? 'GraphQL example'
      : 'SQL example';

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Data Source</h3>
      <div className="space-y-2">
        <Label htmlFor="datasource">Select Data Source</Label>
        <Select
          value={config.dataSourceId as string}
          onValueChange={(value) => onChange('dataSourceId', value)}
        >
          <SelectTrigger id="datasource">
            <SelectValue placeholder="Choose a data source" />
          </SelectTrigger>
          <SelectContent>
            {dataSources.map((ds) => (
              <SelectItem key={ds.id} value={ds.id}>
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  <span>{ds.name}</span>
                  <Badge variant="outline" className="ml-auto">
                    {ds.type}
                  </Badge>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedDataSourceId && (
        <div className="space-y-3 rounded-md border bg-muted/20 p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">{referenceTitle}</p>
            {hints?.databaseName && (
              <Badge variant="outline" className="text-[10px] font-mono">
                {hints.databaseName}
              </Badge>
            )}
          </div>

          {isQueryHintsLoading ? (
            <p className="text-xs text-muted-foreground">Loading query hints...</p>
          ) : (
            <>
              {(hints?.baseUrl || hints?.endpoint || hints?.method) && (
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground">Connection reference</p>
                  <div className="flex flex-wrap gap-1">
                    {hints?.method && (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {hints.method}
                      </Badge>
                    )}
                    {hints?.endpoint && (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {hints.endpoint}
                      </Badge>
                    )}
                    {hints?.baseUrl && (
                      <Badge variant="outline" className="font-mono text-xs">
                        {hints.baseUrl}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {hints?.defaultObject && (
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground">Configured default object</p>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {hints.defaultObject}
                  </Badge>
                </div>
              )}

              {availableObjects.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground">
                    {availableObjectsLabel}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {availableObjects.slice(0, 12).map((objectName) => (
                      <Badge key={objectName} variant="outline" className="font-mono text-xs">
                        {objectName}
                      </Badge>
                    ))}
                    {availableObjects.length > 12 && (
                      <Badge variant="secondary" className="text-xs">
                        +{availableObjects.length - 12} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {queryTemplate && (
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground">{exampleLabel}</p>
                  <pre className="overflow-x-auto rounded border bg-background p-2 text-xs font-mono">
                    {queryTemplate}
                  </pre>
                </div>
              )}

              {queryHints?.message && (
                <p className="text-xs text-amber-600">{queryHints.message}</p>
              )}

              {availableObjects.length === 0 && !queryHints?.message && (
                <p className="text-xs text-muted-foreground">
                  Query hint is not available yet for this datasource.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="query">Custom Query (Optional)</Label>
        <Textarea
          id="query"
          rows={4}
          value={config.query as string || ''}
          onChange={(e) => onChange('query', e.target.value)}
          placeholder={
            queryTemplate
              ? `Example:\n${queryTemplate}`
              : 'Enter SQL query or API query parameters...'
          }
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Leave empty to use default query (fetch all data)
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="limit">Record Limit (Optional)</Label>
        <Input
          id="limit"
          type="number"
          value={config.limit as number || ''}
          onChange={(e) => onChange('limit', e.target.value ? parseInt(e.target.value) : undefined)}
          placeholder="Limit number of records"
        />
      </div>
    </div>
  );
}

// Transformation Node Configuration
function TransformationNodeConfigSection({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const transformations = (config.transformations as any[]) || [];

  const addTransformation = (type: string) => {
    const defaultConfigByType: Record<string, Record<string, unknown>> = {
      RENAME: { sourceField: '', targetField: '', mapping: {} },
      CAST: { field: '', targetType: 'STRING' },
      SPLIT: { field: '', separator: ',', targetFields: [] },
      MERGE: { sourceFields: [], targetField: '', separator: '' },
      JSON_RULES: {
        rules: [
          { op: 'trim', field: 'name' },
          { op: 'cast', field: 'value', targetType: 'NUMBER' },
        ],
      },
      JAVASCRIPT: {
        script: 'return row;',
        timeoutMs: 100,
      },
      lookup_dimension: {
        dimension: 'dim_region',
        localField: '',
        dimensionField: '',
        outputMapping: {},
      },
      calculate_field: {
        targetField: '',
        expression: '',
      },
    };
    const newTransform = {
      id: `transform-${Date.now()}`,
      type,
      config: defaultConfigByType[type] || {},
    };
    onChange('transformations', [...transformations, newTransform]);
  };

  const removeTransformation = (id: string) => {
    onChange(
      'transformations',
      transformations.filter((t: any) => t.id !== id)
    );
  };

  const updateTransformationConfig = (id: string, partialConfig: Record<string, unknown>) => {
    onChange(
      'transformations',
      transformations.map((t: any) =>
        t.id === id
          ? {
              ...t,
              config: {
                ...(t.config || {}),
                ...partialConfig,
              },
            }
          : t
      )
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Transformations</h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => addTransformation('RENAME')}>
            <Plus className="mr-1 h-3 w-3" />
            Rename
          </Button>
          <Button size="sm" variant="outline" onClick={() => addTransformation('CAST')}>
            <Plus className="mr-1 h-3 w-3" />
            Cast
          </Button>
          <Button size="sm" variant="outline" onClick={() => addTransformation('SPLIT')}>
            <Plus className="mr-1 h-3 w-3" />
            Split
          </Button>
          <Button size="sm" variant="outline" onClick={() => addTransformation('MERGE')}>
            <Plus className="mr-1 h-3 w-3" />
            Merge
          </Button>
          <Button size="sm" variant="outline" onClick={() => addTransformation('JSON_RULES')}>
            <Plus className="mr-1 h-3 w-3" />
            JSON Rules
          </Button>
          <Button size="sm" variant="outline" onClick={() => addTransformation('JAVASCRIPT')}>
            <Plus className="mr-1 h-3 w-3" />
            JavaScript
          </Button>
          <Button size="sm" variant="outline" onClick={() => addTransformation('lookup_dimension')}>
            <Plus className="mr-1 h-3 w-3" />
            Lookup
          </Button>
          <Button size="sm" variant="outline" onClick={() => addTransformation('calculate_field')}>
            <Plus className="mr-1 h-3 w-3" />
            Calculate
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {transformations.length === 0 ? (
          <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            No transformations added yet. Click a button above to add one.
          </div>
        ) : (
          transformations.map((transform: any) => (
            <TransformationConfigItem
              key={transform.id}
              transform={transform}
              onRemove={() => removeTransformation(transform.id)}
              onConfigChange={(nextConfig) => updateTransformationConfig(transform.id, nextConfig)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TransformationConfigItem({
  transform,
  onRemove,
  onConfigChange,
}: {
  transform: any;
  onRemove: () => void;
  onConfigChange: (config: Record<string, unknown>) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const transformConfig =
    transform?.config && typeof transform.config === 'object'
      ? (transform.config as Record<string, unknown>)
      : {};

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
        <Badge>{transform.type}</Badge>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      {isExpanded && (
        <CardContent className="pt-4">
          {transform.type === 'RENAME' && (
            <div className="space-y-3">
              <Label>Rename Field</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Old field name"
                  className="flex-1"
                  value={(transformConfig.sourceField as string) || ''}
                  onChange={(e) => {
                    const sourceField = e.target.value;
                    const targetField = (transformConfig.targetField as string) || '';
                    onConfigChange({
                      sourceField,
                      targetField,
                      mapping:
                        sourceField && targetField
                          ? { [sourceField]: targetField }
                          : {},
                    });
                  }}
                />
                <span className="flex items-center text-muted-foreground">→</span>
                <Input
                  placeholder="New field name"
                  className="flex-1"
                  value={(transformConfig.targetField as string) || ''}
                  onChange={(e) => {
                    const targetField = e.target.value;
                    const sourceField = (transformConfig.sourceField as string) || '';
                    onConfigChange({
                      sourceField,
                      targetField,
                      mapping:
                        sourceField && targetField
                          ? { [sourceField]: targetField }
                          : {},
                    });
                  }}
                />
              </div>
            </div>
          )}
          {transform.type === 'CAST' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Field to Cast</Label>
                <Input
                  placeholder="field_name"
                  value={(transformConfig.field as string) || ''}
                  onChange={(e) => onConfigChange({ field: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Target Type</Label>
                <Select
                  value={(transformConfig.targetType as string) || 'STRING'}
                  onValueChange={(value) => onConfigChange({ targetType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select data type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STRING">String</SelectItem>
                    <SelectItem value="NUMBER">Number</SelectItem>
                    <SelectItem value="BOOLEAN">Boolean</SelectItem>
                    <SelectItem value="DATE">Date</SelectItem>
                    <SelectItem value="JSON">JSON</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {transform.type === 'SPLIT' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Field to Split</Label>
                <Input
                  placeholder="field_name"
                  value={(transformConfig.field as string) || ''}
                  onChange={(e) => onConfigChange({ field: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Separator</Label>
                <Input
                  placeholder="e.g., ',', '-', ' '"
                  value={(transformConfig.separator as string) || ','}
                  onChange={(e) => onConfigChange({ separator: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Target Fields</Label>
                <Input
                  placeholder="field_1, field_2, field_3"
                  value={Array.isArray(transformConfig.targetFields) ? (transformConfig.targetFields as string[]).join(', ') : ''}
                  onChange={(e) =>
                    onConfigChange({
                      targetFields: e.target.value
                        .split(',')
                        .map((field) => field.trim())
                        .filter((field) => field.length > 0),
                    })
                  }
                />
              </div>
            </div>
          )}
          {transform.type === 'MERGE' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Source Fields to Merge</Label>
                <Input
                  placeholder="field_1, field_2"
                  value={Array.isArray(transformConfig.sourceFields) ? (transformConfig.sourceFields as string[]).join(', ') : ''}
                  onChange={(e) =>
                    onConfigChange({
                      sourceFields: e.target.value
                        .split(',')
                        .map((field) => field.trim())
                        .filter((field) => field.length > 0),
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Target Field Name</Label>
                <Input
                  placeholder="merged_field"
                  value={(transformConfig.targetField as string) || ''}
                  onChange={(e) => onConfigChange({ targetField: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Separator (Optional)</Label>
                <Input
                  placeholder="e.g., ' ', '-', ''"
                  value={(transformConfig.separator as string) || ''}
                  onChange={(e) => onConfigChange({ separator: e.target.value })}
                />
              </div>
            </div>
          )}
          {transform.type === 'JSON_RULES' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Rules (JSON Array)</Label>
                <Textarea
                  rows={10}
                  className="font-mono text-xs"
                  value={(() => {
                    const rules = transformConfig.rules ?? transformConfig.jsonRules ?? [];
                    if (typeof rules === 'string') {
                      return rules;
                    }
                    try {
                      return JSON.stringify(rules, null, 2);
                    } catch {
                      return '[]';
                    }
                  })()}
                  onChange={(e) => {
                    const raw = e.target.value;
                    try {
                      const parsed = JSON.parse(raw);
                      if (Array.isArray(parsed)) {
                        onConfigChange({ rules: parsed });
                        return;
                      }
                      onConfigChange({ rules: raw });
                    } catch {
                      onConfigChange({ rules: raw });
                    }
                  }}
                  placeholder={`[
  { "op": "trim", "field": "name" },
  { "op": "cast", "field": "value", "targetType": "NUMBER" },
  { "op": "default", "field": "status", "value": "ACTIVE" }
]`}
                />
                <p className="text-xs text-muted-foreground">
                  Supported ops: <code>rename</code>, <code>cast</code>, <code>trim</code>, <code>replace</code>, <code>default</code>, <code>drop</code>, <code>set</code>.
                </p>
              </div>
            </div>
          )}
          {transform.type === 'JAVASCRIPT' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Script</Label>
                <Textarea
                  rows={8}
                  className="font-mono text-xs"
                  value={(transformConfig.script as string) || 'return row;'}
                  onChange={(e) => onConfigChange({ script: e.target.value })}
                  placeholder={`const age = helpers.toNumber(row.age, 0);
return {
  ...row,
  age_group: age >= 30 ? "senior" : "junior",
};`}
                />
                <p className="text-xs text-muted-foreground">
                  Return a row object, <code>null</code>, or <code>undefined</code>. Helpers: <code>toNumber</code>, <code>toString</code>, <code>trim</code>, <code>isEmpty</code>.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Timeout (ms)</Label>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={
                    typeof transformConfig.timeoutMs === 'number'
                      ? String(transformConfig.timeoutMs)
                      : ''
                  }
                  placeholder="100"
                  onChange={(e) => {
                    const numeric = Number(e.target.value);
                    onConfigChange({
                      timeoutMs:
                        Number.isFinite(numeric) && numeric > 0
                          ? Math.min(Math.floor(numeric), 1000)
                          : undefined,
                    });
                  }}
                />
              </div>
            </div>
          )}
          {transform.type === 'lookup_dimension' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Dimension Table</Label>
                <Select
                  value={(transformConfig.dimension as string) || 'dim_region'}
                  onValueChange={(value) => onConfigChange({ dimension: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select dimension table" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dim_date">dim_date</SelectItem>
                    <SelectItem value="dim_region">dim_region</SelectItem>
                    <SelectItem value="dim_customer">dim_customer</SelectItem>
                    <SelectItem value="dim_product">dim_product</SelectItem>
                    <SelectItem value="dim_channel">dim_channel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>Local Field</Label>
                  <Input
                    placeholder="province_code"
                    value={(transformConfig.localField as string) || ''}
                    onChange={(e) => onConfigChange({ localField: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dimension Field</Label>
                  <Input
                    placeholder="province_code"
                    value={(transformConfig.dimensionField as string) || ''}
                    onChange={(e) => onConfigChange({ dimensionField: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Output Mapping</Label>
                <Input
                  placeholder="province_name:province_name,country_name:country_name"
                  value={(() => {
                    const mapping = transformConfig.outputMapping;
                    if (!mapping || typeof mapping !== 'object' || Array.isArray(mapping)) {
                      return '';
                    }
                    return Object.entries(mapping as Record<string, unknown>)
                      .map(([source, target]) => `${source}:${String(target)}`)
                      .join(',');
                  })()}
                  onChange={(e) => {
                    const outputMapping: Record<string, string> = {};
                    e.target.value
                      .split(',')
                      .map((pair) => pair.trim())
                      .filter((pair) => pair.length > 0 && pair.includes(':'))
                      .forEach((pair) => {
                        const [source, target] = pair.split(':').map((item) => item.trim());
                        if (source && target) {
                          outputMapping[source] = target;
                        }
                      });
                    onConfigChange({ outputMapping });
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Format: <code>source_column:target_field</code>, pisahkan dengan koma.
                </p>
              </div>
            </div>
          )}
          {transform.type === 'calculate_field' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Target Field</Label>
                <Input
                  placeholder="profit"
                  value={(transformConfig.targetField as string) || ''}
                  onChange={(e) => onConfigChange({ targetField: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Expression</Label>
                <Input
                  placeholder="revenue - cost"
                  value={(transformConfig.expression as string) || ''}
                  onChange={(e) => onConfigChange({ expression: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Mendukung operator aritmetika: +, -, *, /, %, dan tanda kurung.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// Filter Node Configuration
function FilterNodeConfigSection({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const conditions = (config.conditions as any[]) || [];
  const operator = (config.operator as string) || 'AND';

  const addCondition = () => {
    onChange('conditions', [
      ...conditions,
      { field: '', operator: 'eq', value: '' },
    ]);
  };

  const removeCondition = (index: number) => {
    onChange('conditions', conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, updates: Record<string, unknown>) => {
    onChange(
      'conditions',
      conditions.map((c, i) => (i === index ? { ...c, ...updates } : c))
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Filter Conditions</h3>
      <div className="space-y-2">
        <Label>Operator</Label>
        <Select value={operator} onValueChange={(value) => onChange('operator', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AND">AND (All conditions must match)</SelectItem>
            <SelectItem value="OR">OR (Any condition can match)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Conditions</Label>
          <Button size="sm" onClick={addCondition}>
            <Plus className="mr-1 h-3 w-3" />
            Add Condition
          </Button>
        </div>
        {conditions.map((condition, index) => (
          <div key={index} className="flex gap-2 items-center">
            <Input
              placeholder="Field"
              value={condition.field}
              onChange={(e) => updateCondition(index, { field: e.target.value })}
              className="flex-1"
            />
            <Select
              value={condition.operator}
              onValueChange={(value) => updateCondition(index, { operator: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="eq">=</SelectItem>
                <SelectItem value="neq">≠</SelectItem>
                <SelectItem value="gt">&gt;</SelectItem>
                <SelectItem value="gte">≥</SelectItem>
                <SelectItem value="lt">&lt;</SelectItem>
                <SelectItem value="lte">≤</SelectItem>
                <SelectItem value="in">∈</SelectItem>
                <SelectItem value="nin">∉</SelectItem>
                <SelectItem value="like">like</SelectItem>
                <SelectItem value="contains">contains</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Value"
              value={condition.value as string}
              onChange={(e) => updateCondition(index, { value: e.target.value })}
              className="flex-1"
            />
            <Button size="sm" variant="ghost" onClick={() => removeCondition(index)}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Aggregation Node Configuration
function AggregationNodeConfigSection({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const groupBy = (config.groupBy as string[]) || [];
  const aggregations = (config.aggregations as any[]) || [];

  const addAggregation = () => {
    onChange('aggregations', [
      ...aggregations,
      { field: '', function: 'COUNT', alias: '' },
    ]);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Group By Fields</Label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const field = prompt('Enter field name to group by:');
              if (field) onChange('groupBy', [...groupBy, field]);
            }}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Field
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {groupBy.map((field, index) => (
            <Badge key={index} variant="secondary" className="pl-2 pr-1">
              {field}
              <button
                onClick={() =>
                  onChange('groupBy', groupBy.filter((_, i) => i !== index))
                }
                className="ml-2 hover:text-destructive"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Aggregations</Label>
          <Button size="sm" onClick={addAggregation}>
            <Plus className="mr-1 h-3 w-3" />
            Add Aggregation
          </Button>
        </div>
        {aggregations.map((agg, index) => (
          <div key={index} className="grid grid-cols-4 gap-2">
            <Input
              placeholder="Field"
              value={agg.field}
              onChange={(e) => {
                const newAggs = [...aggregations];
                newAggs[index].field = e.target.value;
                onChange('aggregations', newAggs);
              }}
            />
            <Select
              value={agg.function}
              onValueChange={(value) => {
                const newAggs = [...aggregations];
                newAggs[index].function = value;
                onChange('aggregations', newAggs);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SUM">Sum</SelectItem>
                <SelectItem value="AVG">Average</SelectItem>
                <SelectItem value="COUNT">Count</SelectItem>
                <SelectItem value="MIN">Min</SelectItem>
                <SelectItem value="MAX">Max</SelectItem>
                <SelectItem value="DISTINCT_COUNT">Distinct Count</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Alias"
              value={agg.alias}
              onChange={(e) => {
                const newAggs = [...aggregations];
                newAggs[index].alias = e.target.value;
                onChange('aggregations', newAggs);
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onChange('aggregations', aggregations.filter((_, i) => i !== index));
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Join Node Configuration (Placeholder)
function JoinNodeConfigSection({
  config,
  onChange,
  pipelineId,
  nodeId,
  edges,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  pipelineId: string;
  nodeId: string;
  edges: Array<{ id: string; sourceId: string; targetId: string }>;
}) {
  // Find parent nodes (nodes that connect TO this JOIN node)
  const parentEdges = edges.filter((e) => e.targetId === nodeId);
  const [leftParent, rightParent] = parentEdges.slice(0, 2);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Join Configuration</h3>

      {parentEdges.length < 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            ⚠️ JOIN node requires exactly 2 input connections.
            {parentEdges.length === 0 && ' Please connect 2 nodes to this JOIN node.'}
            {parentEdges.length === 1 && ' Please connect 1 more node to this JOIN node.'}
          </p>
        </div>
      ) : (
        <>
          {/* Join Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="join-type">Join Type</Label>
            <Select
              value={config.joinType as string || 'INNER'}
              onValueChange={(value) => onChange('joinType', value)}
            >
              <SelectTrigger id="join-type">
                <SelectValue placeholder="Select join type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INNER">INNER - Only matching records</SelectItem>
                <SelectItem value="LEFT">LEFT - All from left + matching from right</SelectItem>
                <SelectItem value="RIGHT">RIGHT - All from right + matching from left</SelectItem>
                <SelectItem value="FULL">FULL - All records from both</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Left Key Selection */}
          {leftParent && (
            <FieldSelector
              label="Left Join Key"
              pipelineId={pipelineId}
              nodeId={leftParent.sourceId}
              value={config.leftKey as string}
              onChange={(fieldName) => onChange('leftKey', fieldName)}
              fieldTypes={['string', 'number', 'integer']}
              placeholder="Select field from left source..."
              required={true}
            />
          )}

          {/* Right Key Selection */}
          {rightParent && (
            <FieldSelector
              label="Right Join Key"
              pipelineId={pipelineId}
              nodeId={rightParent.sourceId}
              value={config.rightKey as string}
              onChange={(fieldName) => onChange('rightKey', fieldName)}
              fieldTypes={['string', 'number', 'integer']}
              placeholder="Select field from right source..."
              required={true}
            />
          )}

          {/* Help Text */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Select fields that contain matching values in both datasets.
              For example, if joining by region code, choose the field that contains region codes in both sources.
            </p>
          </div>

          {/* Field Type Validation Warning */}
          {config.leftKey && config.rightKey && (
            <div className="text-xs text-muted-foreground">
              ✓ Join keys selected. Make sure the selected fields contain comparable values.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Union Node Configuration
function UnionNodeConfigSection({
  config,
  onChange,
  nodeId,
  edges,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  nodeId: string;
  edges: Array<{ id: string; sourceId: string; targetId: string }>;
}) {
  // Find parent nodes (nodes that connect TO this UNION node)
  const parentEdges = edges.filter((e) => e.targetId === nodeId);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Union Configuration</h3>

      {parentEdges.length < 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            ⚠️ UNION node requires at least 2 input connections.
            {parentEdges.length === 0 && ' Please connect 2 or more nodes to this UNION node.'}
            {parentEdges.length === 1 && ' Please connect at least 1 more node to this UNION node.'}
          </p>
        </div>
      ) : (
        <>
          {/* Union Mode Selection */}
          <div className="space-y-2">
            <Label htmlFor="union-mode">Union Mode</Label>
            <Select
              value={config.unionMode as string || 'ALL'}
              onValueChange={(value) => onChange('unionMode', value)}
            >
              <SelectTrigger id="union-mode">
                <SelectValue placeholder="Select union mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">ALL - Keep all rows (including duplicates)</SelectItem>
                <SelectItem value="DISTINCT">DISTINCT - Remove duplicate rows</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Input Summary */}
          <div className="p-3 bg-teal-50 border border-teal-200 rounded-md">
            <p className="text-sm text-teal-800 font-medium mb-2">
              📊 Connected Inputs: {parentEdges.length}
            </p>
            <ul className="text-xs text-teal-700 space-y-1">
              {parentEdges.map((edge, idx) => (
                <li key={edge.id}>
                  • Input {idx + 1}: Node {edge.sourceId}
                </li>
              ))}
            </ul>
          </div>

          {/* Help Text */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> UNION stacks rows from all inputs.
              Use <strong>ALL</strong> to keep duplicates (faster) or <strong>DISTINCT</strong> to remove duplicates (slower).
            </p>
          </div>

          {/* Mode Explanation */}
          {config.unionMode && (
            <div className="text-xs text-muted-foreground">
              {config.unionMode === 'ALL'
                ? '✓ ALL mode selected: All rows will be included, including duplicates.'
                : '✓ DISTINCT mode selected: Duplicate rows will be removed.'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Destination Node Configuration
function DestinationNodeConfigSection({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">Destination Settings</h3>

      <div className="space-y-2">
        <Label htmlFor="target-table">Target Table Name</Label>
        <Input
          id="target-table"
          value={config.targetTable as string || ''}
          onChange={(e) => onChange('targetTable', e.target.value)}
          placeholder="e.g., transformed_users"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="write-mode">Write Mode</Label>
        <Select
          value={config.writeMode as string}
          onValueChange={(value) => onChange('writeMode', value)}
        >
          <SelectTrigger id="write-mode">
            <SelectValue placeholder="Select write mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="APPEND">Append - Add to existing data</SelectItem>
            <SelectItem value="OVERWRITE">Overwrite - Replace all data</SelectItem>
            <SelectItem value="UPSERT">Upsert - Update or insert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(config.writeMode === 'UPSERT') && (
        <div className="space-y-2">
          <Label>Upsert Key Fields</Label>
          <Input
            placeholder="e.g., id or field1,field2"
            onChange={(e) => onChange('upsertKey', e.target.value.split(','))}
          />
          <p className="text-xs text-muted-foreground">
            Comma-separated field names to match for upsert
          </p>
        </div>
      )}

      <div className="space-y-3 pt-3 border-t">
        <h4 className="text-sm font-medium">Publishing Options</h4>
        <div className="flex items-center justify-between">
          <Label htmlFor="is-public">Make data publicly accessible</Label>
          <Switch
            id="is-public"
            checked={config.isPublic as boolean || false}
            onCheckedChange={(checked) => onChange('isPublic', checked)}
          />
        </div>

        {(config.isPublic as boolean) && (
          <>
            <div className="space-y-2">
              <Label htmlFor="expires">Expiration Date (Optional)</Label>
              <Input
                id="expires"
                type="datetime-local"
                value={config.expiresAt as string || ''}
                onChange={(e) => onChange('expiresAt', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty for no expiration
              </p>
            </div>
          </>
        )}
      </div>

      {(config.isPublic as boolean) && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">API Key Access</p>
                <p className="text-blue-700">
                  An API key will be generated after pipeline execution. You can use it to access this data via REST API.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
